import React, { useEffect, useRef, useState, useCallback } from "react";
import { format, differenceInDays } from "date-fns";
import { useProfile } from "../context/ProfileContext";

// Helper function to clean unwanted commas/spaces
const cleanText = (text) =>
  typeof text === "string" ? text.replace(/^[,\s]+|[,\s]+$/g, "") : text;

export default function POSInvoice({
  shouldDownloadPdf = false,
  invoice = {},
  onSavePdfComplete = null,
}) {
  const data = invoice?.data || invoice || {};

  const containerRef = useRef(null);
  const [generating, setGenerating] = useState(false);
  const [hasDownloaded, setHasDownloaded] = useState(false);
  const downloadTriggeredRef = useRef(false);
  const { profileData } = useProfile();

  // Get cover image URL from profile context
  const logoImage = profileData?.logo
    ? profileData.logo
    : "/img/invoice_logo.png";

  // Extract vendor address from officeAddress
  const vendorAddress = profileData?.address?.officeAddress;
  const vendorFullAddress = vendorAddress?.full_address || "—";
  const vendorCity = vendorAddress?.city || "";
  const vendorState = vendorAddress?.state || "";
  const vendorPincode = vendorAddress?.pincode || "";

  // Format the address nicely
  const formattedVendorAddress = [
    vendorFullAddress,
    vendorCity,
    vendorState,
    vendorPincode,
  ]
    .filter(Boolean)
    .join(", ");

  // --- POS Invoice Specific Data Extraction ---
  const currency = "₹";
  // Vendor: prefer backend vendor_info, fallback to vendor, then profile data
  const vendorInfo = data?.vendor_info || data?.vendor || {};
  const vendorCompanyName =
    vendorInfo.vendor_company_name ||
    vendorInfo.vendor_name ||
    vendorInfo.name ||
    profileData?.profile?.companyName ||
    profileData?.profile?.name ||
    "";
  const vendorPhone =
    vendorInfo.vendor_phone ||
    vendorInfo.phone ||
    profileData?.profile?.phone ||
    "";
  const vendorGst =
    vendorInfo.gstNumber || profileData?.profile?.gstNumber || "";

  // Client: support both top-level client_* fields and nested vendor_client
  const clientSource = data?.vendor_client || {};
  const client = {
    client_first_name:
      data?.client_first_name ||
      clientSource.first_name ||
      clientSource.firstname ||
      clientSource.client_name ||
      "",
    client_last_name:
      data?.client_last_name ||
      clientSource.last_name ||
      clientSource.lastname ||
      "",
    client_phone: data?.client_phone || clientSource.phone || "",
  };

  // Support both backend shape (order_items) and client-side transformed shape (items)
  const rawItems = data?.order_items || data?.items || [];
  const items = Array.isArray(rawItems) ? rawItems : [];

  // Calculate total for POS
  const totalAmount = Number(data?.total_amount || 0);
  const totalGst = Number(data?.total_gst || 0);
  const totalDiscount = Number(data?.total_discount || 0);
  const totalPaid = Number(data?.total_paid_amount || 0);
  const balanceDue = totalAmount - totalPaid;

  // POS specific: Get payment method from payments array
  const getPaymentMethod = () => {
    if (
      data?.payments &&
      Array.isArray(data.payments) &&
      data.payments.length > 0
    ) {
      return data.payments[0]?.payment_mode || "Cash";
    }
    return data?.payment_mode || "Cash";
  };

  const paymentMethod = getPaymentMethod();

  const formatDate = (d) => {
    try {
      return d ? format(new Date(d), "MMM dd, yyyy") : "-";
    } catch {
      return "-";
    }
  };

  const formatDateDMY = (d) => {
    try {
      if (!d) return "—";
      const dt = new Date(d);
      if (isNaN(dt)) return "—";
      return format(dt, "dd/MM/yyyy");
    } catch {
      return "—";
    }
  };

  const formatCurrency = (v) => {
    const n = Number(v || 0);
    return `${currency}${n.toFixed(2)}`;
  };

  const clientAddressLines =
    typeof clientSource?.address === "object" && clientSource?.address !== null
      ? [
          clientSource?.address?.address1,
          clientSource?.address?.address2,
          clientSource?.address?.city,
          clientSource?.address?.state,
          clientSource?.address?.zip,
        ].filter(Boolean)
      : [];
  const clientAddress = data?.client_address || clientAddressLines.join(" ");

  // Get order number for POS - simpler logic
  const getOrderNumber = () => {
    return data.order_number || "—";
  };
  const orderNumber = getOrderNumber();

  // GST calculation for POS products
  // Function for Product GST Table
  const calculateProductGST = () => {
    const gstSummary = {};
    // ONLY filter for products
    items
      .filter((i) => i.item_type === "product")
      .forEach((item) => {
        const lineTotal = Number(item.total_amount || item.total_price || 0);
        const gstRate = Number(item.gst || item.gst_slab || 0);

        if (lineTotal > 0 && gstRate > 0) {
          const taxableValue = lineTotal / (1 + gstRate / 100);
          const gstAmt = lineTotal - taxableValue;

          if (!gstSummary[gstRate])
            gstSummary[gstRate] = { taxable: 0, gst: 0 };
          gstSummary[gstRate].taxable += taxableValue;
          gstSummary[gstRate].gst += gstAmt;
        }
      });

    return Object.entries(gstSummary).map(([rate, data]) => ({
      rate,
      sgst: (data.gst / 2).toFixed(2),
      cgst: (data.gst / 2).toFixed(2),
      totalGST: data.gst.toFixed(2),
    }));
  };
  const SERVICE_TYPES = ["grooming", "vet_clinic", "boarding"];


  // Function for Service Summary Table
  const calculateServiceSummary = () => {
    const serviceSummary = {};
    // ONLY filter for grooming/services
    items
      .filter((i) => SERVICE_TYPES.includes(i.item_type))
      .forEach((item) => {
        const lineTotal = Number(item.total_amount || item.total_price || 0);
        const taxRate = Number(item.gst || item.gst_slab || 0);

        // Calculate service percentage: (gst / total_price) * 100
        const totalPrice = Number(item.total_price || 0);
        const gstAmount = Number(item.gst || 0);
        const servicePercentage =
          totalPrice > 0 ? (gstAmount / totalPrice) * 100 : 0;

        if (lineTotal > 0 && taxRate > 0) {
          const taxableValue = lineTotal / (1 + taxRate / 100);
          const chargeAmt = lineTotal - taxableValue;

          if (!serviceSummary[taxRate])
            serviceSummary[taxRate] = {
              taxable: 0,
              charge: 0,
              servicePercentage: servicePercentage, // Add service percentage here
            };
          serviceSummary[taxRate].taxable += taxableValue;
          serviceSummary[taxRate].charge += chargeAmt;
        }
      });

    return Object.entries(serviceSummary).map(([rate, data]) => ({
      rate,
      servicePercentage: data.servicePercentage.toFixed(2), // Add this field
      taxableAmount: data.taxable.toFixed(2),
      serviceCharge: data.charge.toFixed(2),
    }));
  };

  const productGSTData = calculateProductGST();
  const serviceSummaryData = calculateServiceSummary();

  // Number to words function
  const numberToWords = (num) => {
    if (!num) return "Zero Rupees Only";

    const a = [
      "",
      "One",
      "Two",
      "Three",
      "Four",
      "Five",
      "Six",
      "Seven",
      "Eight",
      "Nine",
      "Ten",
      "Eleven",
      "Twelve",
      "Thirteen",
      "Fourteen",
      "Fifteen",
      "Sixteen",
      "Seventeen",
      "Eighteen",
      "Nineteen",
    ];
    const b = [
      "",
      "",
      "Twenty",
      "Thirty",
      "Forty",
      "Fifty",
      "Sixty",
      "Seventy",
      "Eighty",
      "Ninety",
    ];

    const nToWords = (n, s) => {
      let str = "";
      if (n > 19) str += b[Math.floor(n / 10)] + " " + a[n % 10];
      else str += a[n];
      if (n) str += " " + s;
      return str;
    };

    const intPart = Math.floor(num);
    const decimalPart = Math.round((num - intPart) * 100);

    const crore = Math.floor(intPart / 10000000);
    const lakh = Math.floor((intPart / 100000) % 100);
    const thousand = Math.floor((intPart / 1000) % 100);
    const hundred = Math.floor((intPart / 100) % 10);
    const remainder = Math.floor(intPart % 100);

    let result = "";
    result += nToWords(crore, "Crore ");
    result += nToWords(lakh, "Lakh ");
    result += nToWords(thousand, "Thousand ");
    result += nToWords(hundred, "Hundred ");
    if (intPart > 100 && remainder) result += "and ";
    result += nToWords(remainder, "");

    let finalWords = "Rupees " + result.trim();
    if (decimalPart > 0) {
      finalWords += ` and ${nToWords(decimalPart, "")} Paise`;
    }
    finalWords += " Only";

    return finalWords;
  };

  // Function to get POS item description - simplified
  const getItemDescription = (item) => {
    const itemName = item.service_name || item.description || "Product";
    const itemType = item.item_type === "product" ? "Product" : "Service";
    const sku = item.sku_number
      ? `<span style="font-size: 7pt; color: #6b7280;">SKU: ${item.sku_number}</span>`
      : "";
    const notes = item.notes
      ? `<div style="font-size: 7pt; color: #4b5563; margin-top: 2pt;">${item.notes}</div>`
      : "";

    return `<div>
      <div style="font-weight: 700; color: #1f2937; margin-bottom: 3pt;">
        ${itemName} <span style="font-size: 6pt; color: #9333ea;">(${itemType})</span>
      </div>
      ${sku}
      ${notes}
    </div>`;
  };

  // --- PDF HTML Rendering Functions ---

  const renderHeaderSection = () => `
  <div style="width: 100%; margin-bottom: 20pt; font-family: 'Arial', sans-serif; position: relative;">

    <!-- POS INVOICE - Top Center -->
    <h2 style="text-align: center; font-size: 16pt; font-weight: bold; color: #1f2937; margin: 0 0 15pt 0;">
     TAX INVOICE 
    </h2>

    <!-- Header Row: Logo (Left) + Invoice Info (Right) -->
    <div style="display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 15pt;">

      <!-- Logo & Vendor Info - Left -->
<div style="flex: 1; font-size: 9pt; color: #111827;">

  ${
    logoImage
      ? `<img src="${logoImage}"
          alt="Company Logo"
          style="width: 80pt; object-fit: contain;
          border: 1px solid #d1d5db;
          border-radius: 4pt;
          margin-bottom: 6pt;" />`
      : ""
  }

  <!-- Vendor Details -->
  ${
    vendorCompanyName
      ? `<div style="font-weight: 600; font-size: 10pt;">
          ${cleanText(vendorCompanyName)}
        </div>`
      : ""
  }

  ${
    vendorPhone
      ? `<div style="color: #6b7280;">
          Phone: ${cleanText(vendorPhone)}
        </div>`
      : ""
  }

  ${
    formattedVendorAddress && formattedVendorAddress !== "—"
      ? `<div style="color: #6b7280;">
          Address: ${cleanText(formattedVendorAddress)}
        </div>`
      : ""
  }

  <!-- Bill To -->
  

</div>



      <!-- Invoice info - Right -->
      <div style="text-align: right; font-size: 9pt;">
        <div style="margin-bottom: 4pt;">
          <span style="font-weight: bold; color: #4b5563;">Invoice No: </span>
          <span style="font-weight: 500;">${data.order_number || "—"}</span>
        </div>
        <div style="margin-bottom: 4pt;">
          <span style="font-weight: bold; color: #4b5563;">Date: </span>
          <span style="font-weight: 500;">${formatDate(data.createdAt)}</span>
        </div>
       
      </div>
    </div>

    <!-- Seller Details -->
    <div style="margin-bottom: 15pt;">
      <div style="font-size: 9pt;">
 
        
        ${
          vendorGst
            ? `<p style="color: #6b7280; margin: 2pt 0;">GSTIN: ${cleanText(
                vendorGst
              )}</p>`
            : ""
        }
      </div>
    </div>

    <!-- Client Details for POS -->
    ${
      client.client_first_name || client.client_last_name || client.client_phone
        ? `
        <div>
          <p style="font-weight: bold; font-size: 9pt; color: #4b5563; margin-bottom: 8pt;">BILL TO:</p>
          <div style="font-size: 9pt;">
            ${
              client.client_first_name || client.client_last_name
                ? `<p style="font-weight: 500; color: #1f2937; margin: 2pt 0;">
                  <span style="font-weight: bold;">Name:</span> ${
                    client.client_first_name || ""
                  } ${client.client_last_name || ""}
                </p>`
                : ""
            }
            ${
              client.client_phone
                ? `<p style="color: #6b7280; margin: 2pt 0;">
                  <span style="font-weight: bold;">Phone:</span> ${cleanText(
                    client.client_phone
                  )}
                </p>`
                : ""
            }
            ${
              clientAddress
                ? `<p style="color: #6b7280; margin: 2pt 0;">
                  <span style="font-weight: bold;">Address:</span> ${cleanText(
                    clientAddress
                  )}
                </p>`
                : ""
            }
          </div>
        </div>`
        : `<div style="margin-bottom: 10pt;">
         </div>`
    }
  </div>

  <div style="border-top: 1px solid #d1d5db; margin: 15pt 0;"></div>
`;

  const renderTableHeader = () => `
    <table style="width: 100%; font-size: 8pt; border-collapse: collapse; margin-bottom: 0;">
      <thead style="background-color: #f3f4f6; border-top: 1px solid #d1d5db; border-bottom: 1px solid #d1d5db;">
        <tr>
          <th style="padding: 8pt 5pt; text-align: left; width: 5%; font-weight: bold; color: #374151;">#</th>
          <th style="padding: 8pt 5pt; text-align: left; width: 45%; font-weight: bold; color: #374151;">Item Description</th>
          <th style="padding: 8pt 5pt; text-align: center; width: 10%; font-weight: bold; color: #374151;">MRP</th>
          <th style="padding: 8pt 5pt; text-align: right; width: 15%; font-weight: bold; color: #374151;">Qty</th>
          <th style="padding: 8pt 5pt; text-align: right; width: 15%; font-weight: bold; color: #374151;">Rate(Incl tax)</th>
          <th style="padding: 8pt 5pt; text-align: right; width: 15%; font-weight: bold; color: #374151;">Amount</th>
        </tr>
      </thead>
      <tbody>
  `;

  const renderItemsForPage = (pageNum, itemsPerPage) => {
    const startIndex = pageNum * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const pageItems = items.slice(startIndex, endIndex);

    const rows = pageItems
      .map((item, idx) => {
        const absoluteIndex = startIndex + idx;
        const qty = Number(item?.quantity || 1);
        const price = Number(item?.unit_price || 0);
        const gstPercent = Number(item?.gst || item?.gst_slab || 0);
        const totalPrice = Number(item?.total_amount || price * qty);

        return `
          <tr style="border-bottom: 1px solid #e5e7eb;">
            <td style="padding: 8pt 5pt; text-align: center; color: #6b7280; vertical-align: top;">
              ${absoluteIndex + 1}
            </td>
            <td style="padding: 8pt 5pt; width: 45%; vertical-align: top;">
              ${getItemDescription(item)}
            </td>
             <td style="padding: 8pt 5pt; text-align: right; vertical-align: top;">
              ${formatCurrency(price)}
            </td>
            <td style="padding: 8pt 5pt; text-align: center; color: #374151; white-space: nowrap; vertical-align: top;">
              ${qty}
            </td>
           
            <td style="padding: 8pt 5pt; text-align: right; vertical-align: top;">
              ${formatCurrency(price)}
            </td>
            <td style="padding: 8pt 5pt; text-align: right; vertical-align: top;">
              ${formatCurrency(totalPrice)}
            </td>
          </tr>
        `;
      })
      .join("");

    return rows;
  };

  const renderTotalsSection = () => {
    const subtotal = totalAmount - totalGst;

    return `
    <div style="display: flex; justify-content: flex-end; margin-top: 20pt;">
      <div style="width: 50%;">
        <div style="display: flex; flex-direction: column; gap: 6pt; font-size: 9pt;">
          

       

          ${
            totalDiscount > 0
              ? `<div style="display: flex; justify-content: space-between; color: #dc2626;">
                <span style="color: #4b5563;">Discount:</span>
                <span style="font-weight: 500;">-${formatCurrency(
                  totalDiscount
                )}</span>
              </div>`
              : ""
          }

          <div style="border-top: 1px solid #d1d5db; margin-top: 8pt; padding-top: 8pt;">
            <div style="display: flex; justify-content: space-between; font-weight: bold; font-size: 12pt; color: #9333ea;">
              <span>TOTAL AMOUNT:</span>
              <span>${formatCurrency(totalAmount)}</span>
            </div>
          </div>

        

          ${
            balanceDue > 0
              ? `<div style="display: flex; justify-content: space-between;">
                <span style="color: #4b5563;">Balance Due:</span>
                <span style="font-weight: 500; color: #dc2626;">${formatCurrency(
                  balanceDue
                )}</span>
              </div>`
              : `<div style="display: flex; justify-content: space-between;">
                
               
              </div>`
          }

       
        </div>
      </div>
    </div>

    <!-- Total Amount in Words -->
    <div style="margin-top: 15pt; font-size: 9pt; font-weight: bold; color: #4b5563;">
      <span>Amount in words: </span>
      <span style="font-weight: 500; color: #1f2937;">${numberToWords(
        totalAmount
      )}</span>
    </div>
  `;
  };

  const renderTaxSummary = () => {
    const productHtml =
      productGSTData.length > 0
        ? `
    <div style="margin-bottom: 15pt;">
      <h3 style="font-weight: bold; font-size: 9pt; margin-bottom: 5pt; color: #4b5563;">GST Summary (Products):</h3>
      <table style="width: 100%; border-collapse: collapse; font-size: 8pt; border: 1px solid #d1d5db;">
        <tr style="background-color: #f9fafb; border-bottom: 1px solid #d1d5db;">
          <th style="padding: 6pt; text-align: left;">Rate</th>
          <th style="padding: 6pt; text-align: right;">SGST</th>
          <th style="padding: 6pt; text-align: right;">CGST</th>
          <th style="padding: 6pt; text-align: right;">Total GST</th>
        </tr>
        ${productGSTData
          .map(
            (g) => `
          <tr>
            <td style="padding: 6pt;">${g.rate}%</td>
            <td style="padding: 6pt; text-align: right;">${formatCurrency(
              g.sgst
            )}</td>
            <td style="padding: 6pt; text-align: right;">${formatCurrency(
              g.cgst
            )}</td>
            <td style="padding: 6pt; text-align: right;">${formatCurrency(
              g.totalGST
            )}</td>
          </tr>
        `
          )
          .join("")}
      </table>
    </div>
  `
        : "";

    const serviceHtml =
      serviceSummaryData.length > 0
        ? `
    <div>
      <h3 style="font-weight: bold; font-size: 9pt; margin-bottom: 5pt; color: #4b5563;">Service Summary:</h3>
      <table style="width: 100%; border-collapse: collapse; font-size: 8pt; border: 1px solid #d1d5db;">
        <tr style="background-color: #f9fafb; border-bottom: 1px solid #d1d5db;">
          <th style="padding: 6pt; text-align: left;">Service Charge %</th>
          <th style="padding: 6pt; text-align: right;">Taxable Amount</th>
        </tr>
        ${serviceSummaryData
          .map(
            (s) => `
          <tr>
            <td style="padding: 6pt;">${s.servicePercentage}%</td>
            <td style="padding: 6pt; text-align: right;">${formatCurrency(
              s.rate
            )}</td>
            
          </tr>
        `
          )
          .join("")}
      </table>
    </div>
  `
        : "";

    return `<div style="margin-top: 20pt;">${productHtml}${serviceHtml}</div>`;
  };

  const renderFooterSection = () => `
    <div style="margin-top: 40pt; border-top: 1px solid #e5e7eb; padding-top: 15pt; font-size: 7pt; color: #6b7280;">
      <div style="display: flex; justify-content: space-between; margin-bottom: 10pt;">
       
        
      </div>
      
      <p style="text-align: center; font-style: italic; margin-top: 15pt;">
        **This is a computer generated invoice. No signature required.**
      </p>
      
      <p style="text-align: center; margin-top: 8pt;">
        Thank you for your business! Please visit again.
      </p>
    </div>
  `;

  // --- PDF Generation Logic ---
  const handleSavePdf = useCallback(async () => {
    if (generating || downloadTriggeredRef.current || hasDownloaded) {
      return;
    }

    downloadTriggeredRef.current = true;
    setGenerating(true);
    setHasDownloaded(true);

    try {
      const html2canvas = (await import("html2canvas")).default;
      const { jsPDF } = await import("jspdf");

      const pdf = new jsPDF("p", "pt", "a4");
      const pdfWidth = pdf.internal.pageSize.getWidth();

      const itemsPerPage = 10; // More items per page for POS
      const totalPages = Math.ceil(items.length / itemsPerPage) || 1;

      for (let pageNum = 0; pageNum < totalPages; pageNum++) {
        if (pageNum > 0) {
          pdf.addPage();
        }

        const tempContainer = document.createElement("div");
        tempContainer.style.width = `${pdfWidth}pt`;
        tempContainer.style.padding = "40pt";
        tempContainer.style.fontFamily = "Arial, sans-serif";
        tempContainer.style.backgroundColor = "white";
        document.body.appendChild(tempContainer);

        let pageContent = "";
        const isLastPage = pageNum === totalPages - 1;

        pageContent += renderHeaderSection();
        pageContent += renderTableHeader();
        pageContent += renderItemsForPage(pageNum, itemsPerPage);
        pageContent += "</tbody></table>";

        if (isLastPage) {
          pageContent += renderTotalsSection();
          pageContent += renderTaxSummary();
          pageContent += renderFooterSection();
        } else {
          pageContent += `
            </tbody></table>
            <div style="text-align: center; margin-top: 20pt; font-size: 8pt; color: #6b7280; font-style: italic;">
              Continued on next page...
            </div>
          `;
        }

        tempContainer.innerHTML = pageContent;

        const canvas = await html2canvas(tempContainer, {
          scale: 2,
          useCORS: true,
          logging: false,
          backgroundColor: "#ffffff",
        });

        const imgData = canvas.toDataURL("image/png");
        const imgWidth = pdfWidth;
        const imgHeight = (canvas.height * imgWidth) / canvas.width;

        pdf.addImage(imgData, "PNG", 0, 0, imgWidth, imgHeight);
        document.body.removeChild(tempContainer);
      }

      const filename = `POS_${data.order_number || "invoice"}.pdf`;
      pdf.save(filename);
      onSavePdfComplete?.({ success: true, file: { name: filename } });
    } catch (err) {
      console.error("PDF generation error:", err);
      onSavePdfComplete?.({ success: false, error: err });
    } finally {
      setGenerating(false);
    }
  }, [
    data,
    generating,
    hasDownloaded,
    onSavePdfComplete,
    items,
    totalDiscount,
    totalAmount,
    totalGst,
    totalPaid,
    paymentMethod,
    client,
    totalGst,
    vendorCompanyName,
    vendorPhone,
    vendorGst,
    formattedVendorAddress,
    orderNumber,
    productGSTData,
    serviceSummaryData,
  ]);

  // --- Effects ---
  useEffect(() => {
    setHasDownloaded(false);
    downloadTriggeredRef.current = false;
    setGenerating(false);
  }, [invoice]);

  useEffect(() => {
    if (
      shouldDownloadPdf &&
      !hasDownloaded &&
      !generating &&
      !downloadTriggeredRef.current
    ) {
      handleSavePdf();
    }
  }, [shouldDownloadPdf, hasDownloaded, generating, handleSavePdf]);

  return (
    <div ref={containerRef} style={{ display: "none" }}>
      {/* Invisible container used by html2canvas for PDF rendering */}
    </div>
  );
}
